import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, Globe2Icon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const COUNTRY_BRAND_VIEW = 'https://cdn.magicpatterns.com/uploads/28u29GzJq29aZ71rigybVo/Screenshot_2026-09-11_at_13.56.48.png';
const LENSES = [{
  icon: Globe2Icon,
  title: 'By country',
  detail: 'Compare markets on the same questions to see where a gap is local rather than structural.'
}, {
  icon: Building2Icon,
  title: 'By brand',
  detail: 'Follow one brand across every market it runs in, campaign after campaign.'
}] as const;
export function SlideCountryBrandView({}: SlideProps) {
  return <InteriorSlide surface data-id="element-150" data-mp="3maadl3knyqkj">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(0.75rem,1.5vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-151" data-mp="3m7wmt6x0o0is">
        <div data-id="element-152" data-mp="11hq8am7wqm90">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-153" data-mp="11i9pu1tnb8mi">
            Campaign insight report
          </p>
          <h2 className="text-[clamp(1.9rem,3.2vw,3.1rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-154" data-mp="1ah4sj3xhe0l2">
            The same evidence, seen{' '}
            <span className="text-[var(--brand)]" data-id="element-155" data-mp="1k76hrz1klp1k">by country and by brand.</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-156" data-mp="1p60zexnsx7k3">
          Channel and segment filters turn a two-week run into a ranked list of
          what needs doing, and by whom.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-157" data-mp="1y667la0tk9zb">
        <motion.figure initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="min-h-0 overflow-hidden rounded-2xl border border-black/10 bg-[#1b1024] shadow-sm lg:col-span-8" data-id="element-158" data-mp="3v0j3pgejz10k">
          <img src={COUNTRY_BRAND_VIEW} alt="Snak data report for a Seretide Kenya teaser, filtered by channel and CRM segment, ranking what needs doing and by whom" className="h-full w-full object-contain object-top" data-id="element-159" data-mp="125spwvcr0p0t" />
        </motion.figure>

        <motion.aside initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.28,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-4" data-id="element-160" data-mp="1q1wo93cx76ww">
          {LENSES.map(lens => {
          const Icon = lens.icon;
          return <article key={lens.title} className="rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-161" data-mp="1utybfkbmmi1p">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10" data-id="element-162" data-mp="23uw4x8ofv761">
                  <Icon className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-163" data-mp="2ngy1eo3a07sq" />
                </span>
                <h3 className="mt-3 text-lg font-extrabold text-[var(--fg)]" data-id="element-164" data-mp="2dhz0xssnzigt">
                  {lens.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[var(--muted)]" data-id="element-165" data-mp="2ifs56gaeq2eg">
                  {lens.detail}
                </p>
              </article>;
        })}

          <article className="flex min-h-0 flex-1 flex-col justify-center rounded-2xl bg-[var(--brand)] p-5 text-white shadow-lg" data-id="element-166" data-mp="29gn8zgmbil5g">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/70" data-id="element-167" data-mp="1usk3s9g3ndhp">
              Why it matters
            </p>
            <p className="mt-2 text-sm font-semibold leading-relaxed" data-id="element-168" data-mp="1znz9uf7v853k">
              Every campaign adds evidence to the same view, so the next
              priority is chosen from data rather than opinion.
            </p>
          </article>
        </motion.aside>
      </div>
    </InteriorSlide>;
}